// Simple role -> permissions mapping used by the frontend to control UI actions
import { SETTING_SECTIONS } from './settings';

export const rolePermissions = {
  // Admins have full UI actions including delete; agents do not get delete
  admin: { create: true, edit: true, delete: true },
  agent: { create: true, edit: true, delete: false },
  user: { create: false, edit: false, delete: false },
};

// Helper to load role from localStorage (fallbacks to 'admin') and return permissions
export function getPermissionsForCurrentUser() {
  if (typeof window === 'undefined') return rolePermissions.admin;

  // Prefer explicit per-user permissions stored on the `user` object (set at login)
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.permissions && typeof parsed.permissions === 'object') {
        // ensure returned object has boolean flags for the known permissions
        const perms = {};
        for (const p of availablePermissions) perms[p] = !!parsed.permissions[p];
        return perms;
      }
    }
  } catch (e) {
    // ignore parse errors and fall back to role-based mapping
  }

  // Try to derive roles from JWT token (preferred) to avoid stale localStorage values
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const parts = token.split('.');
      if (parts.length >= 2) {
        let payload = parts[1];
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4) payload += '=';
        const decoded = atob(payload);
        const json = JSON.parse(decodeURIComponent(escape(decoded)));
        const roles = Array.isArray(json.roles) ? json.roles : (json.role ? [json.role] : []);
        if (roles.length) {
          const role = roles[0];
          return rolePermissions[role] || rolePermissions.admin;
        }
      }
    }
  } catch (e) {
    // ignore token parse errors
  }

  // fallback: role-based mapping (legacy)
  const role = localStorage.getItem('role') || 'admin';
  return rolePermissions[role] || rolePermissions.admin;
}

export function isCurrentUserAdmin() {
  if (typeof window === 'undefined') return true;
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      // check both legacy `role` and new `roles` array
      if (parsed && parsed.role) return parsed.role === 'admin';
      if (parsed && Array.isArray(parsed.roles)) return parsed.roles.includes('admin');
    }
  } catch (e) {
    // ignore
  }
  // also check stored roles array (legacy fallback to 'admin' only if nothing present)
  try {
    const rawRoles = localStorage.getItem('roles');
    if (rawRoles) {
      const parsed = JSON.parse(rawRoles);
      if (Array.isArray(parsed)) return parsed.includes('admin');
    }
  } catch (e) {}
  // SAFE fallback: if nothing is stored, do NOT assume admin
  return localStorage.getItem('role') === 'admin';
}

export function isCurrentUserAgent() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.role) return parsed.role === 'agent';
      if (parsed && Array.isArray(parsed.roles)) return parsed.roles.includes('agent');
    }
  } catch (e) {}
  try {
    const rawRoles = localStorage.getItem('roles');
    if (rawRoles) {
      const parsed = JSON.parse(rawRoles);
      if (Array.isArray(parsed)) return parsed.includes('agent');
    }
  } catch (e) {}
  return (localStorage.getItem('role') || '') === 'agent';
}

// Remove 'delete' from the global available permissions to prevent creating delete perms
export const availablePermissions = ['create', 'edit', 'view'];

// Focused employee permission set used by the Roles UI; delete permission removed
export const EMPLOYEE_PERMISSIONS = ['create_employee', 'edit_employee', 'view_employee'];

export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'assign', 'export', 'approve'];

const SETTING_PERMISSION_PAGES = SETTING_SECTIONS.map((section) => ({
  key: section.permissionKey,
  label: section.permissionLabel,
  actions: ['view', 'create', 'edit'],
}));

export const PERMISSION_PAGES = [
  { key: 'dashboard', label: 'Dashboard', actions: ['view', 'export'] },
  { key: 'employees', label: 'Employees', actions: ['view', 'create', 'edit', 'export'] },
  { key: 'employers', label: 'Employers', actions: ['view', 'create', 'edit', 'export'] },
  { key: 'orders', label: 'Orders', actions: ['view', 'create', 'edit', 'assign', 'export'] },
  { key: 'payments', label: 'Payments', actions: ['view', 'create', 'edit', 'export', 'approve'] },
  { key: 'complaints', label: 'Complaints', actions: ['view', 'create', 'edit'] },
  { key: 'blacklist', label: 'Blacklist', actions: ['view', 'create', 'edit'] },
  { key: 'reports', label: 'Reports', actions: ['view', 'export'] },
  { key: 'activity', label: 'Activity Log', actions: ['view', 'export'] },
  ...SETTING_PERMISSION_PAGES,
  { key: 'users', label: 'Users', actions: ['view', 'create', 'edit'] },
  { key: 'roles', label: 'Roles', actions: ['view', 'create', 'edit'] },
];

export const LEGACY_PERMISSION_MAP = {
  create_order: 'orders.create',
  view_order: 'orders.view',
  update_order: 'orders.edit',
  delete_order: 'orders.delete',
  approve_order: 'payments.approve',
  create_payment: 'payments.create',
  view_payment: 'payments.view',
  update_payment: 'payments.edit',
  delete_payment: 'payments.delete',
  create_user: 'users.create',
  view_user: 'users.view',
  update_user: 'users.edit',
  delete_user: 'users.delete',
  manage_roles: 'roles.edit',
  manage_permissions: 'roles.edit',
};

// Backward-compatible exports used elsewhere
export const TABLES = PERMISSION_PAGES.map((page) => page.key);
export const ACTIONS = ['create', 'edit', 'view'];

export function getManagedPermissionKeys() {
  return PERMISSION_PAGES.flatMap((page) =>
    page.actions.map((action) => `${page.key}.${action}`)
  );
}

let canonicalPermissionByLower = null;

function getCanonicalPermissionByLower() {
  if (!canonicalPermissionByLower) {
    canonicalPermissionByLower = Object.fromEntries(
      getManagedPermissionKeys().map((key) => [key.toLowerCase(), key])
    );
  }
  return canonicalPermissionByLower;
}

export function normalizePermissionName(name) {
  const trimmed = String(name || '').trim();
  const lower = trimmed.toLowerCase();
  if (LEGACY_PERMISSION_MAP[lower]) {
    return LEGACY_PERMISSION_MAP[lower];
  }
  return getCanonicalPermissionByLower()[lower] ?? lower;
}

export function normalizePermissionList(list) {
  const managed = new Set(getManagedPermissionKeys());
  const normalized = new Set();

  for (const permission of expandLegacySettingsPermissions(list)) {
    const key = normalizePermissionName(permission);
    if (managed.has(key)) {
      normalized.add(key);
    }
  }

  return [...normalized];
}

export function countManagedPermissions(list) {
  return normalizePermissionList(list).length;
}

export function getPagePermissionKeys(page) {
  return page.actions.map((action) => `${page.key}.${action}`);
}

function expandLegacySettingsPermissions(list) {
  const expanded = new Set(list || []);
  const settingKeys = SETTING_SECTIONS.map((section) => section.permissionKey);

  if (expanded.has('settings.view')) {
    settingKeys.forEach((key) => expanded.add(`${key}.view`));
    expanded.delete('settings.view');
  }

  if (expanded.has('settings.edit')) {
    settingKeys.forEach((key) => {
      expanded.add(`${key}.create`);
      expanded.add(`${key}.edit`);
    });
    expanded.delete('settings.edit');
  }

  if (expanded.has('settings.create')) {
    settingKeys.forEach((key) => expanded.add(`${key}.create`));
    expanded.delete('settings.create');
  }

  return [...expanded];
}

export function permissionGranted(permissions = [], name) {
  if (permissions.includes('__admin__')) return true;
  const target = String(name || '').trim().toLowerCase();
  if (!target) return false;
  return permissions.some((perm) => String(perm).trim().toLowerCase() === target);
}

export function canViewSettingSection(section, permissions = []) {
  if (permissions.includes('__admin__')) return true;
  return permissionGranted(permissions, `${section.permissionKey}.view`);
}

export function getVisibleSettingSections(permissions = []) {
  return SETTING_SECTIONS.filter((section) => canViewSettingSection(section, permissions));
}

// generate permissions like "employees.create", "employees.edit", etc.
export const SYSTEM_PERMISSIONS = getManagedPermissionKeys();
